import { useEffect, useMemo, useRef, useState } from 'react';
import { COOWN_DATE_FILTER_YEAR_HORIZON, COOWN_PROJECT_START_YEAR } from '../../config/constants';

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun',
  '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
};

const formatMonthLabel = (value) => {
  if (!value) return '';
  const [year = '', month = ''] = String(value).split('-');
  if (!year || !month) return value;
  return `${MONTH_LABELS[month] || month} ${year}`;
};

const formatDayLabel = (value) => {
  if (!value) return '';
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const AdminCalendarTrigger = ({
  type,
  value,
  onChange,
  min,
  max,
  placeholder,
  className,
  ariaLabel,
}) => {
  const inputRef = useRef(null);
  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // showPicker can throw if not user-gestured in some browsers
      }
    }
    el.click();
  };

  const display = value
    ? (type === 'month' ? formatMonthLabel(value) : formatDayLabel(value))
    : placeholder;

  return (
    <button
      type="button"
      className={`admin-calendar-trigger${value ? ' admin-calendar-trigger--filled' : ''}`}
      onClick={openPicker}
      aria-label={ariaLabel}
    >
      <i className="fas fa-calendar-alt" aria-hidden="true"></i>
      <span>{display}</span>
      <input
        ref={inputRef}
        type={type}
        className={className}
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        tabIndex={-1}
        aria-hidden="true"
      />
    </button>
  );
};

const LISTED_DATE_MODES = [
  { key: 'all', label: 'Any time', shortLabel: 'Any' },
  { key: 'year', label: 'By year', shortLabel: 'Year' },
  { key: 'month', label: 'By month', shortLabel: 'Month' },
  { key: 'date', label: 'By date', shortLabel: 'Day' },
];

const ADMIN_CALENDAR_DATE_MODES = [
  { key: 'all', label: 'All dates' },
  { key: 'year', label: 'Year' },
  { key: 'month', label: 'Month' },
  { key: 'date', label: 'Day' },
];

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const defaultYearMax = () => new Date().getFullYear() + COOWN_DATE_FILTER_YEAR_HORIZON;

const daysInMonth = (year, month) => {
  const y = Number(year);
  const m = Number(month);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
};

const BrowseListedDateFilter = ({
  dateMode,
  onDateModeChange,
  listedYear,
  onListedYearChange,
  listedMonth,
  onListedMonthChange,
  listedDate,
  onListedDateChange,
  onClear,
  variant = 'browse',
  fieldLabel,
  yearMin = COOWN_PROJECT_START_YEAR,
  yearMax = defaultYearMax(),
  useCalendarInputs = false,
}) => {
  const resolvedFieldLabel = fieldLabel || (variant === 'admin' ? 'Listed' : 'Listed on site');
  const showClear = dateMode !== 'all' && (listedYear || listedMonth || listedDate);
  const rootClass = variant === 'home'
    ? 'browse-listed-filter browse-listed-filter--home'
    : variant === 'admin'
      ? 'live-listings-toolbar__group live-listings-toolbar__group--date browse-listed-filter browse-listed-filter--admin'
      : 'browse-field browse-listed-filter';

  const selectClass = variant === 'admin'
    ? 'live-listings-toolbar__date-input browse-listed-filter__select'
    : 'browse-field-input browse-listed-filter__select';

  const calendarClass = variant === 'admin'
    ? 'live-listings-toolbar__date-input browse-listed-filter__calendar'
    : 'browse-field-input browse-listed-filter__calendar';

  const yearOptions = useMemo(() => {
    const years = [];
    const max = Math.max(yearMin, yearMax);
    for (let y = max; y >= yearMin; y -= 1) {
      years.push(String(y));
    }
    return years;
  }, [yearMin, yearMax]);

  const minMonth = `${yearMin}-01`;
  const maxMonth = `${yearMax}-12`;
  const minDate = `${yearMin}-01-01`;
  const maxDate = `${yearMax}-12-31`;

  const [monthYear, setMonthYear] = useState('');
  const [monthPart, setMonthPart] = useState('');
  const [dateYear, setDateYear] = useState('');
  const [dateMonth, setDateMonth] = useState('');
  const [dateDay, setDateDay] = useState('');

  useEffect(() => {
    if (listedMonth) {
      const [y = '', m = ''] = listedMonth.split('-');
      setMonthYear(y);
      setMonthPart(m);
    } else {
      setMonthYear('');
      setMonthPart('');
    }
  }, [listedMonth, dateMode]);

  useEffect(() => {
    if (listedDate) {
      const [y = '', m = '', d = ''] = listedDate.split('-');
      setDateYear(y);
      setDateMonth(m);
      setDateDay(d);
    } else {
      setDateYear('');
      setDateMonth('');
      setDateDay('');
    }
  }, [listedDate, dateMode]);

  const handleModeChange = (key) => {
    if (key !== 'year') onListedYearChange('');
    if (key !== 'month') onListedMonthChange('');
    if (key !== 'date') onListedDateChange('');
    onDateModeChange(key);
  };

  const dateDayMax = daysInMonth(dateYear, dateMonth);
  const dateDayOptions = Array.from({ length: dateDayMax }, (_, i) => String(i + 1).padStart(2, '0'));

  const modeOptions = useCalendarInputs && variant === 'admin'
    ? ADMIN_CALENDAR_DATE_MODES
    : LISTED_DATE_MODES;

  const useBrowseCalendarPickers = variant === 'browse' || variant === 'home';

  const pendingHint = dateMode === 'year' && !listedYear
    ? `Choose a year to filter ${resolvedFieldLabel.toLowerCase()} dates.`
    : dateMode === 'month' && !listedMonth
      ? useBrowseCalendarPickers
        ? `Choose a month from the picker below.`
        : `Pick a month using the calendar button.`
      : dateMode === 'date' && !listedDate
        ? useBrowseCalendarPickers
          ? `Choose a date from the picker below.`
          : `Pick a day using the calendar button.`
        : null;

  const defaultHint = variant === 'admin'
    ? useCalendarInputs
      ? `Use the calendar pickers — years from ${yearMin} through ${yearMax}.`
      : `Filter by when listings were ${resolvedFieldLabel.toLowerCase()}.`
    : 'Narrow results to fractional buys and rentals listed in a specific period.';

  return (
    <div className={rootClass}>
      <span className={variant === 'admin' ? 'live-listings-toolbar__label' : 'browse-field-label'} id="browse-listed-date-label">
        {resolvedFieldLabel}
      </span>
      <div className={variant === 'admin' ? 'live-listings-toolbar__date browse-listed-filter__controls' : 'browse-listed-filter__controls'}>
        <div className={`browse-listed-filter__modes-row${variant === 'admin' && useCalendarInputs ? ' browse-listed-filter__modes-row--admin-calendar' : ''}`}>
          <div
            className={`browse-mode-segment browse-listed-filter__modes${variant === 'admin' ? ' listings-view-toggle' : ''}`}
            role="tablist"
            aria-labelledby="browse-listed-date-label"
          >
            {modeOptions.map(({ key, label, shortLabel }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={dateMode === key}
                className={dateMode === key ? 'active' : ''}
                onClick={() => handleModeChange(key)}
              >
                {useCalendarInputs && variant === 'admin' ? (
                  label
                ) : variant === 'admin' && key === 'all' ? (
                  'All dates'
                ) : (
                  <>
                    <span className="browse-listed-mode-label browse-listed-mode-label--long">{label}</span>
                    {shortLabel ? (
                      <span className="browse-listed-mode-label browse-listed-mode-label--short" aria-hidden="true">
                        {shortLabel}
                      </span>
                    ) : null}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
        {dateMode !== 'all' ? (
          <div className="browse-listed-filter__pickers">
            {dateMode === 'year' ? (
              <select
                className={selectClass}
                value={listedYear}
                onChange={(e) => onListedYearChange(e.target.value)}
                aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} year`}
              >
                <option value="">Year…</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            ) : null}
            {dateMode === 'month' ? (
              useCalendarInputs ? (
                <AdminCalendarTrigger
                  type="month"
                  className={calendarClass}
                  min={minMonth}
                  max={maxMonth}
                  value={listedMonth}
                  placeholder="Pick month"
                  onChange={(e) => onListedMonthChange(e.target.value)}
                  ariaLabel={`Filter by ${resolvedFieldLabel.toLowerCase()} month`}
                />
              ) : useBrowseCalendarPickers ? (
                <input
                  type="month"
                  className={`${selectClass} browse-listed-filter__calendar`}
                  min={minMonth}
                  max={maxMonth}
                  value={listedMonth}
                  onChange={(e) => onListedMonthChange(e.target.value)}
                  aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} month`}
                />
              ) : (
                <>
                  <select
                    className={selectClass}
                    value={monthYear}
                    onChange={(e) => {
                      const nextYear = e.target.value;
                      setMonthYear(nextYear);
                      onListedMonthChange(nextYear && monthPart ? `${nextYear}-${monthPart}` : '');
                    }}
                    aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} year`}
                  >
                    <option value="">Year</option>
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <select
                    className={selectClass}
                    value={monthPart}
                    onChange={(e) => {
                      const nextMonth = e.target.value;
                      setMonthPart(nextMonth);
                      onListedMonthChange(monthYear && nextMonth ? `${monthYear}-${nextMonth}` : '');
                    }}
                    aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} month`}
                    disabled={!monthYear}
                  >
                    <option value="">Month</option>
                    {MONTH_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </>
              )
            ) : null}
            {dateMode === 'date' ? (
              useCalendarInputs ? (
                <AdminCalendarTrigger
                  type="date"
                  className={calendarClass}
                  min={minDate}
                  max={maxDate}
                  value={listedDate}
                  placeholder="Pick day"
                  onChange={(e) => onListedDateChange(e.target.value)}
                  ariaLabel={`Filter by ${resolvedFieldLabel.toLowerCase()} day`}
                />
              ) : useBrowseCalendarPickers ? (
                <input
                  type="date"
                  className={`${selectClass} browse-listed-filter__calendar`}
                  min={minDate}
                  max={maxDate}
                  value={listedDate}
                  onChange={(e) => onListedDateChange(e.target.value)}
                  aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} day`}
                />
              ) : (
                <>
                  <select
                    className={selectClass}
                    value={dateYear}
                    onChange={(e) => {
                      const nextYear = e.target.value;
                      setDateYear(nextYear);
                      const maxDay = daysInMonth(nextYear, dateMonth);
                      const safeDay = dateDay && Number(dateDay) > maxDay ? String(maxDay).padStart(2, '0') : dateDay;
                      if (safeDay !== dateDay) setDateDay(safeDay);
                      onListedDateChange(nextYear && dateMonth && safeDay ? `${nextYear}-${dateMonth}-${safeDay}` : '');
                    }}
                    aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} year`}
                  >
                    <option value="">Year</option>
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <select
                    className={selectClass}
                    value={dateMonth}
                    onChange={(e) => {
                      const nextMonth = e.target.value;
                      setDateMonth(nextMonth);
                      const maxDay = daysInMonth(dateYear, nextMonth);
                      const safeDay = dateDay && Number(dateDay) > maxDay ? String(maxDay).padStart(2, '0') : dateDay;
                      if (safeDay !== dateDay) setDateDay(safeDay);
                      onListedDateChange(dateYear && nextMonth && safeDay ? `${dateYear}-${nextMonth}-${safeDay}` : '');
                    }}
                    aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} month`}
                    disabled={!dateYear}
                  >
                    <option value="">Month</option>
                    {MONTH_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <select
                    className={selectClass}
                    value={dateDay}
                    onChange={(e) => {
                      const nextDay = e.target.value;
                      setDateDay(nextDay);
                      onListedDateChange(dateYear && dateMonth && nextDay ? `${dateYear}-${dateMonth}-${nextDay}` : '');
                    }}
                    aria-label={`Filter by ${resolvedFieldLabel.toLowerCase()} day`}
                    disabled={!dateYear || !dateMonth}
                  >
                    <option value="">Day</option>
                    {dateDayOptions.map((day) => (
                      <option key={day} value={day}>{Number(day)}</option>
                    ))}
                  </select>
                </>
              )
            ) : null}
            {showClear ? (
              <button
                type="button"
                className={variant === 'admin' ? 'live-listings-toolbar__date-clear' : 'browse-listed-filter__clear'}
                onClick={onClear}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
                Clear
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {variant !== 'home' ? (
        <p className="browse-listed-filter__hint">
          {pendingHint || defaultHint}
        </p>
      ) : null}
    </div>
  );
};

export default BrowseListedDateFilter;
